// Job-number lifecycle → workspace locks (frontend mirror of
// apps/api/src/common/job-number.ts). A PMT job number is a placeholder until
// Sage Intacct assigns a real one; the placeholder prefix drives two locks:
//   SYSTAC-NNN     bid "chance"          → edit-locked (read-only) until Closed-Won
//   ClosedWon-NNN  bid won, no Sage # yet → activity-locked (no requests) until a Sage #
// IMP- (import stub) and MNT- (maintenance) are intentionally NOT locked.
//
// Kept self-contained (no import from the manpower module, which has its own
// isSystacJobNumber/isClosedWonJobNumber) so lib carries no cross-module dep.
// This only drives the UI — the ProjectStageGuard on the API is the real
// enforcement, so a stale client can't slip a write past it.

export const isSystacPlaceholder = (jobNumber: string | null | undefined): boolean =>
  /^SYSTAC-/i.test((jobNumber ?? '').trim());

export const isClosedWonPlaceholder = (jobNumber: string | null | undefined): boolean =>
  /^ClosedWon-/i.test((jobNumber ?? '').trim());

/** A real Sage job number: not blank and not one of the four placeholder prefixes.
 *  Mirror of `isRealJobNumber` in apps/api/src/common/job-number.ts. */
export const isRealJobNumber = (jobNumber: string | null | undefined): boolean => {
  const jn = (jobNumber ?? '').trim();
  return jn !== '' && !/^(IMP|SYSTAC|ClosedWon|MNT)-/i.test(jn);
};

export const EDIT_LOCKED_MESSAGE =
  'This is a SYSTAC bid opportunity — it stays read-only until the bid is won in Bid Tracker, which promotes it to a ClosedWon job automatically.';
export const ACTIVITY_LOCKED_MESSAGE =
  'This project doesn’t have a Sage Intacct job number yet — equipment requests and other project activity unlock once a Sage job number is assigned.';

export interface ProjectStage {
  /** SYSTAC — the whole project is read-only (details, dates, notes, crew). */
  editLocked: boolean;
  /** SYSTAC or ClosedWon — no requests/activity (equipment, RFIs, materials, …). */
  activityLocked: boolean;
  /** Human explanation for the workspace banner, or null when unlocked. */
  reason: string | null;
}

export function projectStage(
  jobNumber: string | null | undefined,
  closedWonAt?: string | null,
): ProjectStage {
  const systacPrefix = isSystacPlaceholder(jobNumber);
  const closedWonPrefix = isClosedWonPlaceholder(jobNumber);
  // Bid Tracker marks a bid Closed-Won by stamping bid_closed_won_at; the number
  // often still reads SYSTAC- until it's renumbered (RENUMBER prompt). A won job
  // is editable — the edit lock is ONLY for a SYSTAC bid not yet won.
  const won = !!closedWonAt;
  const editLocked = systacPrefix && !won;
  // Still no Sage # while the number is a SYSTAC-/ClosedWon- placeholder.
  const activityLocked = systacPrefix || closedWonPrefix;
  return {
    editLocked,
    activityLocked,
    reason: editLocked ? EDIT_LOCKED_MESSAGE : activityLocked ? ACTIVITY_LOCKED_MESSAGE : null,
  };
}
